'use client';

import React, { useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth, StaffRole } from '@/contexts/AuthContext';
import { Loader2, Eye, EyeOff, UserPlus, LogIn, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import { useLanguage } from '@/contexts/LanguageContext';

function LoginPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, signUp } = useAuth();
  const { t } = useLanguage();

  const ROLE_OPTIONS: { value: StaffRole; labelKey: string; descKey: string }[] = [
    { value: 'admin', labelKey: 'login.roles.admin', descKey: 'login.roles.adminDesc' },
    { value: 'cashier', labelKey: 'login.roles.cashier', descKey: 'login.roles.cashierDesc' },
    { value: 'kitchen', labelKey: 'login.roles.kitchen', descKey: 'login.roles.kitchenDesc' },
    { value: 'waiter', labelKey: 'login.roles.waiter', descKey: 'login.roles.waiterDesc' },
  ];

  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<StaffRole>('waiter');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const redirectTo = searchParams.get('redirect') || '/';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await signIn(email, password);
      toast.success(t('login.welcomeToast'));
      router.push(redirectTo);
    } catch (err: any) {
      toast.error(err?.message || t('login.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !fullName) return;
    if (password.length < 6) {
      toast.error(t('login.passwordTooShort'));
      return;
    }
    setLoading(true);
    try {
      await signUp(email, password, { fullName, role });
      toast.success(t('login.signupSuccess'));
      setTab('login');
      setPassword('');
    } catch (err: any) {
      toast.error(err?.message || t('login.signupFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (demoEmail: string, demoPassword: string) => {
    setLoading(true);
    try {
      await signIn(demoEmail, demoPassword);
      toast.success(t('login.welcomeNewToast'));
      router.push(redirectTo);
    } catch (err: any) {
      toast.error(err?.message || t('login.demoLoginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDemoView = async () => {
    setLoading(true);
    try {
      await signIn('admin@nexpos.app', 'nexpos2026');
      // Set demo session expiry: 30 minutes from now
      const expiry = Date.now() + 30 * 60 * 1000;
      localStorage.setItem('demo_session_expiry', String(expiry));
      toast.success('Demo moduna hoş geldiniz! 30 dakika sonra otomatik çıkış yapılacak.');
      router.push(redirectTo);
    } catch (err: any) {
      toast.error(err?.message || 'Demo girişi başarısız oldu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--background)' }}>
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: 480, height: 480, background: 'radial-gradient(circle, rgba(14,165,233,0.06), transparent 70%)' }} />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center rounded-xl"
              style={{ width: 36, height: 36 }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/assets/images/ChatGPT_Image_16_Tem_2026_19_27_19-1784418888639.png"
                alt="NexPos logo"
                style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 8 }}
              />
            </div>
            <span className="text-2xl font-bold tracking-tight" style={{ letterSpacing: '-0.03em' }}>
              Nex<span style={{ color: '#0EA5E9' }}>Pos</span>
            </span>
          </div>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{t('login.tagline')}</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-7" style={{ background: 'var(--card)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
          {/* Tab switcher */}
          <div className="flex rounded-xl p-1 mb-6" style={{ background: 'rgba(255,255,255,0.04)' }}>
            <button
              onClick={() => setTab('login')}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-[10px] text-sm font-semibold transition-all duration-200"
              style={tab === 'login'
                ? { background: 'var(--card)', color: 'var(--foreground)', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }
                : { color: 'var(--muted-foreground)' }}
            >
              <LogIn size={13} /> {t('login.tabSignIn')}
            </button>
            <button
              onClick={() => setTab('signup')}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-[10px] text-sm font-semibold transition-all duration-200"
              style={tab === 'signup'
                ? { background: 'var(--card)', color: 'var(--foreground)', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }
                : { color: 'var(--muted-foreground)' }}
            >
              <UserPlus size={13} /> {t('login.tabSignUp')}
            </button>
          </div>

          {/* ── LOGIN FORM ── */}
          {tab === 'login' && (
            <>
              <h2 className="text-xl font-bold mb-1" style={{ letterSpacing: '-0.02em' }}>{t('login.welcomeBack')}</h2>
              <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>{t('login.signInSubtitle')}</p>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>{t('login.emailLabel')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={t('login.emailPlaceholder')}
                    required
                    className="w-full px-3.5 rounded-xl text-sm focus:outline-none transition-all"
                    style={{ background: 'var(--input)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--foreground)', height: 44 }}
                    onFocus={e => { e.currentTarget.style.borderColor = 'rgba(14,165,233,0.5)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(14,165,233,0.1)'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>{t('login.passwordLabel')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder={t('login.passwordPlaceholder')}
                      required
                      className="w-full px-3.5 pr-11 rounded-xl text-sm focus:outline-none transition-all"
                      style={{ background: 'var(--input)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--foreground)', height: 44 }}
                      onFocus={e => { e.currentTarget.style.borderColor = 'rgba(14,165,233,0.5)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(14,165,233,0.1)'; }}
                      onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.boxShadow = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl font-bold text-sm transition-all duration-200 hover:scale-[0.99] active:scale-[0.97] disabled:opacity-60 flex items-center justify-center gap-2"
                  style={{ background: 'var(--primary)', color: '#fff', height: 48, boxShadow: '0 4px 16px rgba(14,165,233,0.25)' }}
                >
                  {loading ? <><Loader2 size={15} className="animate-spin" /> {t('login.signingIn')}</> : t('login.tabSignIn')}
                </button>
              </form>

              {/* Demo credentials */}
              <div className="mt-6 pt-5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                {/* Demo View Button */}
                <button
                  onClick={handleDemoView}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2.5 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-[0.99] active:scale-[0.97] disabled:opacity-60 mb-4"
                  style={{
                    height: 48,
                    background: 'linear-gradient(135deg, rgba(14,165,233,0.15), rgba(139,92,246,0.15))',
                    border: '1px solid rgba(14,165,233,0.35)',
                    color: '#0EA5E9',
                    boxShadow: '0 4px 16px rgba(14,165,233,0.12)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(14,165,233,0.22), rgba(139,92,246,0.22))'; e.currentTarget.style.borderColor = 'rgba(14,165,233,0.55)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(14,165,233,0.15), rgba(139,92,246,0.15))'; e.currentTarget.style.borderColor = 'rgba(14,165,233,0.35)'; }}
                >
                  {loading ? <Loader2 size={15} className="animate-spin" /> : <PlayCircle size={17} />}
                  Demo Olarak Görüntüle
                </button>

                {/* Demo accounts section removed */}
              </div>
            </>
          )}

          {/* ── SIGNUP FORM ── */}
          {tab === 'signup' && (
            <>
              <h2 className="text-xl font-bold mb-1">{t('login.createAccount')}</h2>
              <p className="text-sm text-muted-foreground mb-6">{t('login.signUpSubtitle')}</p>

              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1.5">{t('login.fullNameLabel')}</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder={t('login.fullNamePlaceholder')}
                    required
                    className="w-full px-3 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1.5">{t('login.emailLabel')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="staff@nexpos.app"
                    required
                    className="w-full px-3 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1.5">{t('login.passwordLabel')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder={t('login.minPasswordHint')}
                      required
                      minLength={6}
                      className="w-full px-3 py-2.5 pr-10 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                      style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1.5">{t('login.roleLabel')}</label>
                  <div className="grid grid-cols-2 gap-2">
                    {ROLE_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setRole(opt.value)}
                        className="flex flex-col items-start px-3 py-2.5 rounded-xl text-left transition-all duration-150"
                        style={{
                          border: role === opt.value ? '1.5px solid #00C4B4' : '1px solid var(--border)',
                          background: role === opt.value ? 'rgba(0,196,180,0.08)' : 'var(--input)',
                        }}
                      >
                        <span className="text-xs font-bold capitalize" style={{ color: role === opt.value ? '#00C4B4' : 'var(--foreground)' }}>
                          {t(opt.labelKey)}
                        </span>
                        <span className="text-2xs text-muted-foreground mt-0.5 leading-tight">{t(opt.descKey)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-[0.99] active:scale-[0.97] disabled:opacity-60 flex items-center justify-center gap-2"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> {t('login.creatingAccount')}</> : t('login.createAccountButton')}
                </button>
              </form>

              {/* Role access info */}
              <div className="mt-5 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{t('login.roleAccessInfo')}</span> {t('login.roleAccessDesc')}
                </p>
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-6 space-y-2">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link href="/gizlilik-politikasi" className="text-xs transition-colors hover:underline" style={{ color: 'var(--muted-foreground)' }}>
              {t('login.privacyPolicy')}
            </Link>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.15)' }}>·</span>
            <Link href="/kullanim-kosullari" className="text-xs transition-colors hover:underline" style={{ color: 'var(--muted-foreground)' }}>
              {t('login.termsOfService')}
            </Link>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.15)' }}>·</span>
            <Link href="/cerez-politikasi" className="text-xs transition-colors hover:underline" style={{ color: 'var(--muted-foreground)' }}>
              {t('login.cookiePolicy')}
            </Link>
          </div>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
            {t('login.copyright')}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}>
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    }>
      <LoginPageInner />
    </Suspense>
  );
}
